import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, Skeleton } from '@ibsys/kit';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCreateUserGroup, useDeleteUserGroup, useUpdateUserGroup, useUserGroupById } from '@/entities/userGroups';
import { useAppNavigate } from '@/shared/lib';
import { UserGroupFromValues, UsersGroupsForm, useUserGroupSchema } from './UsersGroupForm';

type Props = {
  groupId?: number;
  isCreating: boolean;
  onUnsavedChange?: (hasUnsaved: boolean) => void;
};

export const UsersGroupsCard: FC<Props> = ({ groupId, isCreating, onUnsavedChange }) => {
  const { data, isLoading } = useUserGroupById(!isCreating ? groupId : undefined);
  const navigate = useAppNavigate();
  const schema = useUserGroupSchema();

  const defaults: UserGroupFromValues = useMemo(
    () => ({
      name: data?.name ?? '',
      predicate: data?.predicate ?? { dsl: '', dslHuman: '' },
      // Преобразуем UserInGroupResponse[] в Array<{ id: number }>
      users: data?.users?.map((user) => ({ id: user.id })) ?? [],
    }),
    [data],
  );

  const form = useForm<UserGroupFromValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
  });
  const { formState } = form;

  useEffect(() => {
    onUnsavedChange?.(formState.isDirty);
  }, [formState.isDirty, onUnsavedChange]);

  useEffect(() => {
    form.reset(isCreating ? { name: '', predicate: { dsl: '', dslHuman: '' }, users: [] } : defaults);
  }, [form, defaults, isCreating]);

  const { mutateAsync: deleteGroup } = useDeleteUserGroup();
  const { mutateAsync: createUserGroup, isPending: isLoadingCreating } = useCreateUserGroup();
  const { mutateAsync: updateUserGroup, isPending: isLoadingUpdating } = useUpdateUserGroup();

  const isLoadingSaving = isLoadingCreating || isLoadingUpdating;

  const handleSave = async (values: UserGroupFromValues) => {
    const predicate = { dsl: values.predicate?.dsl ?? '', dslHuman: values.predicate?.dslHuman ?? '' };

    if (isCreating) {
      // При создании передаем только id
      const users = values.users?.map((user) => ({ id: user.id })) || null;
      const { id } = await createUserGroup({
        ...values,
        users,
        predicate,
      });
      // Отправленное становится сохранённым: карточка перестаёт считать себя изменённой.
      // Признак снимается сразу, а не следующим рендером: переход на созданную группу идёт
      // синхронно и иначе наткнулся бы на предупреждение о потере уже сохранённого
      form.reset(values);
      onUnsavedChange?.(false);
      navigate('/user-groups/:id', { id: id.toString() });
    } else if (groupId && data) {
      // При обновлении нужно сохранить user2groupId, если он был
      const users =
        values.users?.map((formUser) => {
          // Ищем существующего пользователя в данных API для сохранения user2groupId
          const existingUser = data.users?.find((u) => u.id === formUser.id);
          return {
            id: formUser.id,
            user2groupId: existingUser?.user2groupId ?? null,
          };
        }) || null;
      await updateUserGroup({
        id: groupId,
        body: {
          ...values,
          users,
          predicate,
        },
      });
      form.reset(values);
    }
  };

  const handleUpdateName = async (name: string) => {
    if (!groupId || !data) return;
    await updateUserGroup({
      id: groupId,
      body: {
        name,
        predicate: data.predicate,
        users:
          data.users?.map((u) => ({
            id: u.id,
            user2groupId: u.user2groupId,
          })) || null,
      },
    });
  };

  const handleDelete = async () => {
    if (groupId) {
      await deleteGroup(groupId);
      // Удалённой группе правки уже не принадлежат — возврат к списку о них не предупреждает
      onUnsavedChange?.(false);
      navigate('/user-groups');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="w-45 h-8" />
        </CardHeader>
        <CardContent>
          <Skeleton className="w-45 h-8" />
        </CardContent>
      </Card>
    );
  }

  return (
    <FormProvider {...form}>
      <UsersGroupsForm
        key={`${isCreating}-${groupId}`}
        isCreating={isCreating}
        isLoadingSaving={isLoadingSaving}
        onSave={handleSave}
        onDelete={handleDelete}
        onUpdateName={handleUpdateName}
      />
    </FormProvider>
  );
};
